/**
 * EmailStep Component
 * Step 1: Collect email address and send verification code.
 * Focused, low-friction first step in the signup flow.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, AlertCircle, Loader2 } from 'lucide-react';
import { Button, Input } from '@/components/ui';

interface EmailStepProps {
  email: string;
  error: string | null;
  isLoading: boolean;
  onEmailChange: (email: string) => void;
  onSubmit: () => void;
  canProceed: boolean;
}

export const EmailStep: React.FC<EmailStepProps> = ({
  email,
  error,
  isLoading,
  onEmailChange,
  onSubmit,
  canProceed,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isExistingAccountError = error?.includes('already exists');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canProceed) {
      onSubmit();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-2">
        <h2 className="text-xl font-bold text-slate-900 mb-2">
          {t('signup.whatsYourEmail')}
        </h2>
        <p className="text-slate-600">
          {t('signup.sendVerificationCode')}
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <div className="text-sm">
            <p>{error}</p>
            {isExistingAccountError && (
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="mt-2 font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
              >
                {t('signup.goToSignIn')}
              </button>
            )}
          </div>
        </div>
      )}

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
          {t('signup.emailLabel')}
        </label>
        <Input
          type="email"
          id="email"
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onEmailChange(e.target.value)}
          leftIcon={<Mail className="w-5 h-5" />}
          variant="emerald"
          inputSize="lg"
          placeholder="you@example.com"
          required
          autoComplete="email"
          autoFocus
        />
      </div>

      <Button
        type="submit"
        disabled={!canProceed || isLoading}
        variant="gradient-emerald"
        size="lg"
        shape="rounded"
        fullWidth
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            {t('signup.sendingCode')}
          </span>
        ) : (
          t('signup.continue')
        )}
      </Button>
    </form>
  );
};

export default EmailStep;
